import { useEffect, useMemo, useState } from 'react'
import { Button, Flex, HStack, Box, Heading, Text } from '@chakra-ui/react'
import { LuChevronLeft } from 'react-icons/lu'
import { toast } from '@/components/ui/toaster/toasterHelpers'

import SelectedOrderDetails from '@/components/union-orders/SelectedOrderDetails'
import { SelectedSibCapacity } from '@/components/union-orders/SelectedSibCapacity'
import { getSibBankServices, getUserOrderCapacityYears } from '@/api/settings.api'
import { getOrders, getWorkload } from '@/api/orders.api'
import type { Order, ServiceCode } from '@/common/order.common'
import type { CapacityYear } from '@/common/settings.common'
import {
  addOrderHours,
  capacityUnitsOfYear,
  EMPTY_HOURS,
  hoursToUnits,
  subtractOrderHours,
  sumOrderBuckets,
  unitPriceOn,
  type HoursMap,
} from '@/helper/order.helper'
import type { GetOrderListResponse, WorkloadResponse } from '@/api/dto/order.dto'
import { UnionOrderOverviewSection } from './UnionOrderOverviewSection'

/**
 * The order under review: its SIB, the planning year it was ordered for, and the calendar date
 * its period starts. Capacity is checked per planning year and prices resolve per start date
 * (SIB-1110), so neither may fall back to today's values.
 */
type OrderSpecifier = { year: number; sibId: string; startDate: string }

/** Union manager "Bestellungen" section: order overview plus the per-SIB review view. */
export function UnionOrdersPage() {
  const [orders, setOrders] = useState<GetOrderListResponse['items']>({})
  const [reloadOrdersFlag, setReloadOrdersFlag] = useState(0)
  const [, setIsLoadingOrders] = useState(true)

  const [selectedOrderSpecifier, setSelectedOrderSpecifier] = useState<OrderSpecifier | null>(null)
  const [capacityYears, setCapacityYears] = useState<CapacityYear[] | null>(null)
  const [workload, setWorkload] = useState<WorkloadResponse | null>(null)
  const [expandedOrder, setExpandedOrder] = useState<Order | null>(null)
  const [sibOrders, setSibOrders] = useState<Order[]>([])
  const [useAllOpenOrders, setUseAllOpenOrders] = useState(true)

  useEffect(() => {
    async function load() {
      setIsLoadingOrders(true)
      try {
        const data = await getOrders({ status: 'SUBMITTED' })
        setOrders(data.items ?? {})
      } catch {
        toast.error('Bestellungen konnten nicht geladen werden.')
      } finally {
        setIsLoadingOrders(false)
      }
    }
    load()
  }, [reloadOrdersFlag])

  useEffect(() => {
    if (!selectedOrderSpecifier) return

    const year = selectedOrderSpecifier.year
    if (!year) return

    const sibId = selectedOrderSpecifier.sibId
    const fromIso = `${year}-01-01T00:00:00.000`
    const toIso = `${year}-12-31T23:59:59.999`

    const loadCapacityYears = async () => {
      try {
        const res = await getUserOrderCapacityYears(sibId)
        setCapacityYears(res.years ?? [])
      } catch (e) {
        console.error(e)
        toast.error('Kapazitätseinstellungen konnten nicht geladen werden.')
      }
    }

    const loadWorkload = async () => {
      try {
        const workload = await getWorkload({ userId: sibId, from: fromIso, to: toIso })
        setWorkload(workload)
      } catch (e) {
        console.error(e)
        toast.error('Workload-Daten konnten nicht geladen werden.')
      }
    }

    const loadSibOrders = async () => {
      try {
        const data = await getOrders({ userId: sibId, year: [year] })
        const yearItems = data.items?.[year] ?? {}
        setSibOrders(Object.values(yearItems).flat())
      } catch (e) {
        console.error(e)
      }
    }

    setSibOrders([])
    setCapacityYears(null)
    loadCapacityYears()
    loadWorkload()
    loadSibOrders()
  }, [selectedOrderSpecifier, reloadOrdersFlag])

  // The capacity agreed for the order's planning year, not the one in effect today (SIB-1110).
  const agreedCapacity: HoursMap | null = useMemo(
    () =>
      selectedOrderSpecifier
        ? capacityUnitsOfYear(capacityYears, selectedOrderSpecifier.year)
        : null,
    [capacityYears, selectedOrderSpecifier]
  )

  const remainingCurrentYear: HoursMap | null = useMemo(() => {
    if (!agreedCapacity) return null
    if (!workload || !Array.isArray(workload.buckets)) return agreedCapacity

    const remaining = { ...EMPTY_HOURS }

    workload.buckets.forEach(b => {
      const hours = Number(b.remainingCapacityHours ?? 0)
      remaining[b.serviceType] = hoursToUnits(b.serviceType, hours)
    })

    return remaining
  }, [agreedCapacity, workload])

  const remainingAfterApproval: HoursMap | null = useMemo(() => {
    if (!remainingCurrentYear || !selectedOrderSpecifier) return remainingCurrentYear

    const openOrdersForCalc = useAllOpenOrders
      ? sibOrders.filter(o => o.status === 'SUBMITTED')
      : expandedOrder
        ? [expandedOrder]
        : []

    const openUnits = openOrdersForCalc.reduce(
      (sum, o) => {
        const buckets = sumOrderBuckets(o.buckets)
        return {
          TOJ: sum.TOJ + hoursToUnits('TOJ', buckets.TOJ ?? 0),
          CONSULTING: sum.CONSULTING + hoursToUnits('CONSULTING', buckets.CONSULTING ?? 0),
          TRAINING: sum.TRAINING + hoursToUnits('TRAINING', buckets.TRAINING ?? 0),
          PILOT: (sum.PILOT ?? 0) + hoursToUnits('PILOT', buckets.PILOT ?? 0),
        }
      },
      { ...EMPTY_HOURS }
    )

    return subtractOrderHours(remainingCurrentYear, openUnits)
  }, [remainingCurrentYear, selectedOrderSpecifier, useAllOpenOrders, sibOrders, expandedOrder])

  // --- Aggregates for the right-hand capacity panel -------------------------
  const openSibOrders = useMemo(() => sibOrders.filter(o => o.status === 'SUBMITTED'), [sibOrders])

  const openOrdersUnits: HoursMap = useMemo(() => {
    const totalHours = openSibOrders.reduce(
      (acc, o) => addOrderHours(acc, sumOrderBuckets(o.buckets)),
      { ...EMPTY_HOURS }
    )
    return {
      TOJ: hoursToUnits('TOJ', totalHours.TOJ),
      CONSULTING: hoursToUnits('CONSULTING', totalHours.CONSULTING),
      TRAINING: hoursToUnits('TRAINING', totalHours.TRAINING),
      PILOT: hoursToUnits('PILOT', totalHours.PILOT ?? 0),
    }
  }, [openSibOrders])

  const openOrdersGross = useMemo(
    () => openSibOrders.reduce((sum, o) => sum + (o.grossAmount ?? 0), 0),
    [openSibOrders]
  )

  const detailSibName = useMemo(() => {
    if (!selectedOrderSpecifier) return 'SIB'
    return (
      sibOrders[0]?.username ??
      orders[selectedOrderSpecifier.year]?.[selectedOrderSpecifier.sibId]?.[0]?.username ??
      'SIB'
    )
  }, [selectedOrderSpecifier, sibOrders, orders])

  // Per-service unit prices (brutto) are the SIB's agreed prices with the Union (bank 1), fetched
  // per SIB so managers see each SIB's own pricing. Each is the price effective on the day the
  // order period starts, so an agreed later change counts for an order in that period (SIB-1110).
  const [unitPrices, setUnitPrices] = useState<Partial<Record<ServiceCode, number>> | null>(null)

  useEffect(() => {
    const sibId = selectedOrderSpecifier?.sibId
    const startDate = selectedOrderSpecifier?.startDate
    if (!sibId || !startDate) {
      setUnitPrices(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await getSibBankServices(sibId)
        if (cancelled) return
        const map: Partial<Record<ServiceCode, number>> = {}
        ;(res.items ?? []).forEach(s => {
          map[s.serviceCode as ServiceCode] = unitPriceOn(s, startDate)
        })
        setUnitPrices(map)
      } catch {
        if (!cancelled) setUnitPrices(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [selectedOrderSpecifier])

  return (
    <>
      {selectedOrderSpecifier ? (
        <Flex w="full" p={4} direction="column" gap={4}>
          <HStack>
            <Button variant="ghost" size="sm" onClick={() => setSelectedOrderSpecifier(null)}>
              <LuChevronLeft />
              <Text>Zurück</Text>
            </Button>
          </HStack>

          <Heading size="lg" fontWeight="normal">
            Bestellung prüfen
          </Heading>

          <Flex gap={4} align="flex-start" pb="88px">
            <Box
              flex="1"
              borderWidth="1px"
              borderColor="border"
              borderRadius="xl"
              bg="bg.panel"
              p={4}
            >
              <SelectedOrderDetails
                onRefreshOrders={() => setReloadOrdersFlag(f => f + 1)}
                sibId={selectedOrderSpecifier.sibId}
                year={selectedOrderSpecifier.year}
                sibName={detailSibName}
                onExpandedOrderChange={setExpandedOrder}
                agreedCapacityUnits={agreedCapacity ?? EMPTY_HOURS}
                remainingCurrentYearUnits={remainingCurrentYear ?? EMPTY_HOURS}
              />
            </Box>

            <Box
              w="360px"
              flexShrink={0}
              borderWidth="1px"
              borderColor="border"
              borderRadius="xl"
              bg="bg.panel"
              p={4}
            >
              <SelectedSibCapacity
                sibName={detailSibName}
                agreedCapacity={agreedCapacity}
                remainingCurrentYear={
                  useAllOpenOrders ? remainingAfterApproval : remainingCurrentYear
                }
                openOrdersUnits={openOrdersUnits}
                openOrdersGross={openOrdersGross}
                unitPrices={unitPrices}
                useAllOpenOrders={useAllOpenOrders}
                onToggle={setUseAllOpenOrders}
              />
            </Box>
          </Flex>
        </Flex>
      ) : (
        <Box w="full" p={4}>
          <UnionOrderOverviewSection
            onSelectOrder={order =>
              setSelectedOrderSpecifier({
                year: new Date(order.startTime).getFullYear(),
                sibId: order.userId,
                startDate: order.startTime.slice(0, 10),
              })
            }
          />
        </Box>
      )}
    </>
  )
}
